import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';

// Set up fonts for pdfMake
(pdfMake as any).vfs = pdfFonts;

interface WorktimeEntry {
  ID: number;
  Datum: number;
  Beginn: number;
  Ende: number;
  Pause: number;
  Bemerkung: string;
  Arbeitszeit: number;
}

const formatDuration = (ms: number): string => {
  const totalMinutes = Math.floor(Number(ms) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export function generateTimesheetPDF(
  workerName: string,
  month: moment.Moment,
  entries: WorktimeEntry[]
) {
  // Filter and sort entries for the given month
  const monthEntries = entries
    .filter(e => {
      const d = moment(Number(e.Datum));
      return d.month() === month.month() && d.year() === month.year();
    })
    .sort((a, b) => Number(a.Datum) - Number(b.Datum) || Number(a.Beginn) - Number(b.Beginn));

  // Calculate total work time
  const totalMs = monthEntries.reduce((sum, e) => sum + Number(e.Arbeitszeit), 0);

  // Build table rows
  const tableBody: any[][] = [
    [
      { text: 'Datum', style: 'headerCells' },
      { text: 'Beginn', style: 'headerCells' },
      { text: 'Ende', style: 'headerCells' },
      { text: 'Pause', style: 'headerCells' },
      { text: 'Arbeitszeit', style: 'headerCells' },
      { text: 'Bemerkung', style: 'headerCells' },
    ]
  ];

  monthEntries.forEach(entry => {
    tableBody.push([
      { text: moment(Number(entry.Datum)).format('DD.MM.'), style: 'nameColumn' },
      { text: formatDuration(entry.Beginn), style: 'euroColumn' },
      { text: formatDuration(entry.Ende), style: 'euroColumn' },
      { text: formatDuration(entry.Pause), style: 'euroColumn' },
      { text: formatDuration(entry.Arbeitszeit), style: 'euroColumn' },
      { text: entry.Bemerkung || '', style: 'nameColumn' },
    ]);
  });

  const docDefinition: any = {
    pageSize: 'A4',
    pageMargins: [40, 90, 30, 90],

    header: (currentPage: number, pageCount: number) => ({
      margin: [40, 30, 30, 30],
      columns: [
        {
          width: '*',
          text: 'Den Weg mit Pferden gehen\nBarriser Weg 64\n24787 Fockbek',
          style: 'company'
        },
        {
          width: 150,
          text: moment().format('DD.MM.YYYY') +
            '\nTel.: 01523/3697043\nmail: waltherj@web.de\nSeite ' +
            currentPage.toString() + ' von ' + pageCount,
          style: 'company',
          alignment: 'right'
        }
      ]
    }),

    footer: () => ({
      margin: [40, 30, 30, 30],
      columns: [
        {
          width: '*',
          text: 'Den Weg mit Pferden gehen\nDaniela Daniels',
          style: 'companySmall'
        },
        {
          width: '*',
          text: 'Sparkasse Mittelholstein\nIBAN DE23 2145 0000 0003 4137 60',
          style: 'companySmall'
        },
        {
          width: '*',
          text: 'Finanzamt Rendsburg\nStnr. 20/290/50928',
          style: 'companySmall'
        }
      ]
    }),

    content: [
      {
        text: ' Arbeitszeiten von ' + workerName + ' im Monat ' + month.format('MMMM YYYY'),
        style: 'header'
      },
      {
        margin: [0, 0, 0, 20],
        table: {
          headerRows: 1,
          widths: ['*', '*', '*', '*', '*', 200],
          body: tableBody,
        },
        layout: {
          hLineWidth: (i: number) => (i === 1) ? 3 : 1,
          hLineColor: (i: number) => (i === 1) ? 'black' : 'gray',
          vLineWidth: () => 1,
          vLineColor: () => 'grey',
          fillColor: (rowIndex: number) => (rowIndex === 0) ? '#418441' : null,
        },
      },
      {
        text: ' Summe Arbeitszeiten ' + formatDuration(totalMs),
        style: 'header'
      },
      {
        text: '\n\ngenehmigt:',
        style: 'nameColumn'
      },
    ],

    styles: {
      company: {
        color: 'black',
        fontSize: 10,
      },
      companySmall: {
        color: 'grey',
        fontSize: 8,
        alignment: 'center',
      },
      header: {
        bold: true,
        color: '#ee0000',
        fontSize: 12,
      },
      nameColumn: {
        alignment: 'left',
        margin: [5, 5, 10, 10],
        fontSize: 10,
      },
      euroColumn: {
        alignment: 'right',
        margin: [5, 5, 10, 10],
        fontSize: 10,
      },
      headerCells: {
        bold: true,
        color: 'white',
      },
    },
  };

  const fileName = `Stundenzettel_${workerName.replace(/\s/g, '_')}_${month.format('YYYY-MM')}.pdf`;
  pdfMake.createPdf(docDefinition).download(fileName);
}
